/**
 * DecisionEase — Auth API Client
 * Handles registration, login, token refresh, and logout.
 */

const API_BASE = "/api/auth";

export interface AuthUser {
  id: string;
  email: string | null;
  display_name: string;
  avatar_url: string | null;
  auth_provider: string;
  tone_preference: string;
  theme: string;
  onboarding_complete: boolean;
}

export interface TokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  user: AuthUser;
}

function extractErrorMessage(detail: unknown, fallback: string): string {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    // Pydantic validation errors: [{msg: "...", loc: [...], ...}]
    return detail.map((e: { msg?: string }) => e.msg || "Validation error").join(". ");
  }
  return fallback;
}

export async function registerUser(
  email: string,
  password: string,
  displayName?: string
): Promise<TokenResponse> {
  const res = await fetch(`${API_BASE}/register`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      email,
      password,
      display_name: displayName || "Friend",
    }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(extractErrorMessage(err.detail, "Registration failed"));
  }
  return res.json();
}

export async function loginUser(
  email: string,
  password: string
): Promise<TokenResponse> {
  const res = await fetch(`${API_BASE}/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(extractErrorMessage(err.detail, "Invalid email or password"));
  }
  return res.json();
}

export async function refreshToken(
  refreshToken: string
): Promise<TokenResponse> {
  const res = await fetch(`${API_BASE}/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh_token: refreshToken }),
  });
  if (!res.ok) {
    throw new Error("Token refresh failed");
  }
  return res.json();
}

export async function logoutUser(refreshTokenValue: string): Promise<void> {
  await fetch(`${API_BASE}/logout`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh_token: refreshTokenValue }),
  });
}

export async function getMe(accessToken: string): Promise<AuthUser> {
  const res = await fetch(`${API_BASE}/me`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok) throw new Error("Not authenticated");
  return res.json();
}

export function getGoogleAuthUrl(): string {
  return "/api/auth/google";
}

export async function getGoogleConnectUrl(accessToken?: string | null): Promise<string> {
  const token =
    accessToken ??
    (typeof window !== "undefined" ? localStorage.getItem("de_access_token") : null);

  const res = await fetch(`${API_BASE}/google/connect`, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(extractErrorMessage(err.detail, "Google connection failed"));
  }

  const data = (await res.json()) as { url?: string };
  if (!data.url) throw new Error("Google connection failed");
  return data.url;
}
